import { motion, AnimatePresence } from "framer-motion";
import {
  Users, Plus, Sparkles, Brain, Globe,
  MessageSquare, ChevronLeft, ChevronRight, Zap, Shield, LogOut
} from "lucide-react";
import { useState } from "react";

type NavView = "lobby" | "create" | "edit" | "messages" | "chat" | "trainer" | "rooms" | "admin";

interface SidebarProps {
  currentView: NavView;
  onNavigate: (view: NavView) => void;
  ollamaOnline: boolean;
  currentUser: { username: string; role: string } | null;
  onLogout: () => void;
  guestConversationCount?: number;
}

const mainNavItems: { id: NavView; label: string; icon: typeof Users; badge?: string; adminOnly?: boolean }[] = [
  { id: "lobby", label: "角色大厅", icon: Globe, badge: "HOT" },
  { id: "create", label: "创建角色", icon: Plus },
  { id: "rooms", label: "多人聊天", icon: Users, badge: "NEW" },
  { id: "messages", label: "消息", icon: MessageSquare },
  { id: "trainer", label: "模型设置", icon: Brain },
  { id: "admin", label: "管理后台", icon: Shield, adminOnly: true },
];

export function Sidebar({ currentView, onNavigate, ollamaOnline, currentUser, onLogout, guestConversationCount = 0 }: SidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const isAdmin = currentUser?.role === "admin";

  return (
    <motion.aside
      initial={false}
      animate={{ width: isCollapsed ? 72 : 260 }}
      className="h-full bg-slate-900 border-r border-slate-800 flex flex-col flex-shrink-0 relative overflow-hidden"
    >
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-24 w-6 h-6 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-slate-200 hover:bg-slate-700 transition-colors z-10"
      >
        {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>

      {/* Logo */}
      <div className={`border-b border-slate-800 ${isCollapsed ? "px-3" : "px-4"} py-4`}>
        <div className={`flex items-center ${isCollapsed ? "justify-center" : "gap-3"}`}>
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 via-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0"
          >
            <Sparkles className="w-6 h-6 text-white" />
          </motion.div>
          {!isCollapsed && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <h1 className="text-lg font-bold bg-gradient-to-r from-rose-400 to-purple-400 bg-clip-text text-transparent">Lok AI</h1>
              <div className="flex items-center gap-1">
                <div className={`w-2 h-2 rounded-full ${ollamaOnline ? "bg-green-500" : "bg-red-500"}`} />
                <p className="text-xs text-slate-400">{ollamaOnline ? "Ollama 已连接" : "Ollama 未连接"}</p>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className={`${isCollapsed ? "px-2" : "px-3"} py-3 flex-1 overflow-y-auto`}>
        <nav className="space-y-1">
          {mainNavItems.filter((item) => !item.adminOnly || isAdmin).map((item) => {
            const Icon = item.icon;
            // Highlight both "chat" (when in a chat view) and "messages" nav items
            const isActive = currentView === item.id || (item.id === "messages" && currentView === "chat");
            return (
              <motion.button
                key={item.id}
                whileHover={{ x: isCollapsed ? 0 : 4 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onNavigate(item.id)}
                className={`w-full flex items-center ${isCollapsed ? "justify-center px-2" : "px-2"} py-2.5 rounded-xl transition-all ${
                  isActive
                    ? "bg-gradient-to-r from-rose-500/20 to-purple-500/20 text-white border border-rose-500/30"
                    : "text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                }`}
              >
                <div className={`flex items-center ${isCollapsed ? "justify-center" : "gap-3"}`}>
                  <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? "text-rose-400" : ""}`} />
                  {!isCollapsed && (
                    <span className="text-sm font-medium">{item.label}</span>
                  )}
                </div>
              </motion.button>
            );
          })}
        </nav>
      </div>

      {/* Bottom */}
      <div className={`border-t border-slate-800 ${isCollapsed ? "px-2" : "px-4"} py-3 flex-shrink-0`}>
        {!isCollapsed && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 px-2">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-rose-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                {currentUser?.username?.charAt(0).toUpperCase() || "?"}
              </div>
              <span className="text-sm text-slate-300 truncate">{currentUser?.username || "未知"}</span>
              {isAdmin && <span className="px-1.5 py-0.5 rounded text-[10px] bg-purple-500/20 text-purple-400 font-medium">管理</span>}
            </div>
            <div className="flex items-center gap-2 px-2">
              <Zap className="w-4 h-4 text-amber-400" />
              <span className="text-xs text-slate-400">Lok AI v5.1</span>
            </div>
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-slate-800 transition-colors text-sm"
            >
              <LogOut className="w-4 h-4" />
              退出登录
            </button>
          </div>
        )}
        {isCollapsed && (
          <button onClick={onLogout} className="w-full flex items-center justify-center p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-slate-800 transition-colors" title="退出登录">
            <LogOut className="w-4 h-4" />
          </button>
        )}
      </div>
    </motion.aside>
  );
}
